import * as matchHistoryRepository from "../repositories/matchHistoryRepository";
import * as playerRepository from "../repositories/playerRepository";
import * as matchRepository from "../repositories/matchRepository";
import { buildCanonicalMatchPayload } from "../domain/matchHistory/buildMatchHistoryPayload";
import { buildParticipantSnapshots } from "../features/registry/utils/courtTeams";
import { buildMatchCardViewModel } from "../domain/matchHistory/matchHistoryPresentation";
import { buildMatchDetailViewModel } from "../domain/matchHistory/buildMatchDetailViewModel";
import {
  buildCareerImpactForMatch,
} from "../domain/matchHistory/buildMatchIntelligence";
import {
  projectAchievementFramework,
  projectCareerMilestones,
  projectPersonalBests,
  projectBadmintonSportAchievements,
  projectTennisSportAchievements,
  projectPickleballSportAchievements,
  projectTableTennisSportAchievements,
} from "../domain/player/playerAchievementFramework";
import { calculateCareerStatistics } from "../domain/player/playerCareer";
import { enrichCareerStatistics } from "../domain/player/playerIntelligence";
import { normalizeSportId, DEFAULT_SPORT, SPORT_IDS } from "../domain/sport/sportTypes";
import { toMillis } from "../repositories/matchHistoryRepository";
import { extractPlayerPhotoUrl } from "../repositories/playerPhotoUrlCache";

/** @typedef {import('../domain/matchHistory/matchHistoryTypes').PlayerHistoryQuery} PlayerHistoryQuery */
/** @typedef {import('../domain/matchHistory/matchHistoryTypes').MatchHistoryListItem} MatchHistoryListItem */

/**
 * @param {import('../domain/matchHistory/matchHistoryTypes').MatchParticipant[]} rows
 */
/**
 * Live profile lookup — used only when participant snapshots lack labels (legacy rows).
 * @param {import('../domain/matchHistory/matchHistoryTypes').MatchParticipant[]} rows
 */
async function resolvePlayerProfileFallback(rows) {
  const ids = new Set();
  for (const row of rows || []) {
    if (row.playerId) ids.add(row.playerId);
    if (row.partnerPlayerId) ids.add(row.partnerPlayerId);
  }
  /** @type {Record<string, string>} */
  const nameByPlayerId = {};
  /** @type {Record<string, string | null>} */
  const avatarByPlayerId = {};
  await Promise.all(
    [...ids].map(async (playerId) => {
      try {
        const player = await playerRepository.findById(playerId);
        if (player?.displayName) nameByPlayerId[playerId] = player.displayName;
        const photo = extractPlayerPhotoUrl(player);
        if (photo) avatarByPlayerId[playerId] = photo;
      } catch {
        /* offline / missing — fall back in presentation */
      }
    })
  );
  return { nameByPlayerId, avatarByPlayerId };
}

/**
 * Career impact strip for Match Intelligence (projection-only, capped history read).
 * @param {{
 *   matchId: string,
 *   viewerPlayerId: string,
 *   record: object,
 *   completedAtMs: number,
 *   participants: object[],
 * }} args
 */
async function projectCareerImpactForMatchDetail(args) {
  const { matchId, viewerPlayerId, record, completedAtMs, participants } = args;
  const mine = (participants || []).find((p) => p.playerId === viewerPlayerId);
  if (!mine) return [];

  const { items: cards } = await getPlayerMatchHistoryCards({
    playerId: viewerPlayerId,
    limit: 48,
  });
  const stats = enrichCareerStatistics(calculateCareerStatistics(cards), cards);
  const sportId = normalizeSportId(record.sportId || DEFAULT_SPORT);
  const sportAchievements =
    sportId === SPORT_IDS.BADMINTON
      ? projectBadmintonSportAchievements(cards)
      : sportId === SPORT_IDS.TENNIS
        ? projectTennisSportAchievements(cards)
        : sportId === SPORT_IDS.PICKLEBALL
          ? projectPickleballSportAchievements(cards)
          : sportId === SPORT_IDS.TABLE_TENNIS
            ? projectTableTennisSportAchievements(cards)
            : [];
  const achievementFramework = projectAchievementFramework(stats, cards, sportAchievements);
  const milestones = projectCareerMilestones(stats, cards);
  const personalBests = projectPersonalBests(stats, cards, sportId);

  return buildCareerImpactForMatch({
    matchId,
    completedAt: completedAtMs,
    sportId,
    isWin: mine.result === "Win" || mine.team === record.winnerSide,
    durationSec: record.durationSec ?? null,
    cards,
    achievementFramework,
    milestones,
    personalBests,
    stats,
  });
}

/**
 * @param {Record<string, import('../domain/matchHistory/matchHistoryTypes').MatchRecord>} recordsByMatchId
 */
async function resolveSessionRoomLabels(recordsByMatchId) {
  const ids = new Set();
  Object.values(recordsByMatchId || {}).forEach((rec) => {
    if (rec?.sessionRoomId) ids.add(rec.sessionRoomId);
  });
  /** @type {Record<string, string>} */
  const labels = {};
  await Promise.all(
    [...ids].map(async (roomId) => {
      try {
        const match = await matchRepository.fetchMatch(roomId);
        labels[roomId] = match?.displayName || match?.title || roomId;
      } catch {
        labels[roomId] = roomId;
      }
    })
  );
  return labels;
}

/**
 * @param {import('../domain/matchHistory/matchHistoryTypes').MatchParticipant[]} participantRows
 * @param {Record<string, import('../domain/matchHistory/matchHistoryTypes').MatchRecord>} [recordsByMatchId]
 * @param {import('../domain/matchHistory/matchHistoryTypes').MatchParticipant[]} [allParticipants]
 * @param {Record<string, string>} [nameByPlayerId]
 */
async function buildCardViewModelsFromParticipantRows(
  participantRows,
  recordsByMatchId = {},
  allParticipants = [],
  nameByPlayerId = {},
  roomLabelBySessionId = {}
) {
  const byMatch = new Map();
  for (const p of allParticipants) {
    const list = byMatch.get(p.matchId) || [];
    list.push(p);
    byMatch.set(p.matchId, list);
  }

  return participantRows.map((row) => {
    const record = recordsByMatchId[row.matchId] || null;
    const sessionRoomId = record?.sessionRoomId ?? null;
    return buildMatchCardViewModel({
      myParticipant: row,
      record,
      allParticipants: byMatch.get(row.matchId) || [],
      nameByPlayerId,
      sessionRoomId,
      sessionRoomLabel: sessionRoomId
        ? roomLabelBySessionId[sessionRoomId] || sessionRoomId
        : null,
    });
  });
}

/**
 * @param {PlayerHistoryQuery} query
 */
export async function getPlayerMatchHistoryCards(query) {
  const pageSize = query.limit ?? 20;
  const { items: participantRows, lastDoc } =
    await matchHistoryRepository.listParticipantsForPlayer(query);

  const matchIds = [...new Set(participantRows.map((r) => r.matchId))];
  const [allParticipants, records] = await Promise.all([
    matchHistoryRepository.listParticipantsForMatchIds(matchIds),
    Promise.all(matchIds.map((id) => matchHistoryRepository.findRecordById(id))),
  ]);

  /** @type {Record<string, import('../domain/matchHistory/matchHistoryTypes').MatchRecord>} */
  const recordsByMatchId = {};
  records.forEach((rec) => {
    if (rec) recordsByMatchId[rec.matchId] = rec;
  });

  const { nameByPlayerId, avatarByPlayerId } = await resolvePlayerProfileFallback([
    ...participantRows,
    ...allParticipants,
  ]);

  const roomLabelBySessionId = await resolveSessionRoomLabels(recordsByMatchId);

  const cards = await buildCardViewModelsFromParticipantRows(
    participantRows,
    recordsByMatchId,
    allParticipants,
    nameByPlayerId,
    roomLabelBySessionId
  );

  return {
    items: cards,
    lastDoc,
    hasMore: participantRows.length >= Math.min(pageSize, 50),
  };
}

/**
 * Persist a completed live session match to canonical history collections.
 * Idempotent on `idempotencyKey` (room + completion time + winner).
 * Repairs orphan records that have no participant rows.
 */
export async function persistCompletedMatch(input) {
  if (!input?.createdBy) return null;

  let snapshots = input.participantSnapshots;
  if (!snapshots?.length) {
    snapshots = buildParticipantSnapshots(
      input.teamA,
      input.teamB,
      input.registryEntries || []
    );
  }

  const { record, participants, idempotencyKey } = buildCanonicalMatchPayload({
    ...input,
    participantSnapshots: snapshots,
  });

  if (!participants.length) {
    console.warn(
      "[matchHistory] persistCompletedMatch skipped — no participant snapshots",
      input.sessionRoomId
    );
    return null;
  }

  const existing = await matchHistoryRepository.findRecordByIdempotencyKey(idempotencyKey);
  if (existing) {
    const existingParts = await matchHistoryRepository.listParticipantsForMatch(
      existing.matchId
    );
    if (!existingParts.length) {
      const repaired = participants.map((p) => ({
        ...p,
        matchId: existing.matchId,
      }));
      await matchHistoryRepository.createParticipants(repaired);
    }
    return existing;
  }

  const created = await matchHistoryRepository.createRecord(record);
  await matchHistoryRepository.createParticipants(participants);
  return created;
}

/**
 * @param {PlayerHistoryQuery} query
 */
export async function getPlayerMatchHistory(query) {
  const { items, lastDoc, hasMore } = await getPlayerMatchHistoryCards(query);
  /** @type {MatchHistoryListItem[]} */
  const list = items.map((card) => ({
    matchId: card.matchId,
    sportId: card.sportId || null,
    matchType: card.matchType,
    completedAt: card.completedAt,
    result: card.scoreSummary,
    participantResult: card.participantResult,
    scoreSummary: card.scoreSummary,
    competitionId: card.competitionId ?? null,
    clubId: card.clubId,
    opponentsLabel: card.opponentsLabel,
    resultHeadline: card.resultHeadline,
    durationSec: card.durationSec,
    isWin: card.isWin,
  }));
  return { items: list, lastDoc, hasMore };
}

/**
 * @param {string} playerId
 * @param {number} [limit]
 */
export async function getRecentMatchesForPlayer(playerId, limit = 5, sportId = null) {
  const { items } = await getPlayerMatchHistoryCards({ playerId, limit, sportId });
  return items;
}

/**
 * @param {string} matchId
 */
export async function getMatchDetail(matchId) {
  const record = await matchHistoryRepository.findRecordById(matchId);
  if (!record) return null;
  const participants = await matchHistoryRepository.listParticipantsForMatch(matchId);
  return {
    record,
    participants,
    completedAt: toMillis(record.completedAtMs ?? record.completedAt),
  };
}

/**
 * @param {string} matchId
 * @param {{ viewerPlayerId?: string | null }} [options]
 */
export async function getMatchDetailView(matchId, options = {}) {
  const detail = await getMatchDetail(matchId);
  if (!detail) return null;
  const { nameByPlayerId, avatarByPlayerId } = await resolvePlayerProfileFallback(
    detail.participants
  );

  const viewerPlayerId = options.viewerPlayerId || null;
  let careerImpact = [];
  if (viewerPlayerId) {
    try {
      careerImpact = await projectCareerImpactForMatchDetail({
        matchId,
        viewerPlayerId,
        record: detail.record,
        completedAtMs: detail.completedAt,
        participants: detail.participants,
      });
    } catch {
      careerImpact = [];
    }
  }

  return buildMatchDetailViewModel({
    record: detail.record,
    participants: detail.participants,
    completedAtMs: detail.completedAt,
    nameByPlayerId,
    avatarByPlayerId,
    viewerPlayerId,
    careerImpact,
  });
}

/**
 * Import room-scoped history into canonical collections (best-effort backfill).
 * Prefer live idempotency keys (`room:date:winner`) so re-import matches a later live write.
 * @param {string} sessionRoomId
 * @param {unknown[]} roomHistory
 * @param {string} createdBy
 * @param {unknown[]} [registryEntries]
 * @param {{ sportId?: string }} [options]
 */
export async function importLegacyRoomHistory(
  sessionRoomId,
  roomHistory,
  createdBy,
  registryEntries = [],
  options = {}
) {
  if (!sessionRoomId || !createdBy || !Array.isArray(roomHistory)) return [];

  const imported = [];
  const patched = [];
  for (let i = 0; i < roomHistory.length; i += 1) {
    const entry = roomHistory[i];
    if (!entry?.teamA || !entry?.teamB || !entry?.winner) continue;
    if (entry.source === "managed-fixture") continue;
    if (entry.canonicalMatchId) {
      patched.push(entry);
      continue;
    }

    const completedAtMs = Number(entry.date || entry.completedAtMs) || Date.now();
    const liveKey = `${sessionRoomId}:${completedAtMs}:${entry.winner}`;
    const legacyKey = `${sessionRoomId}:legacy:${i}:${entry.winner}`;

    let existing =
      (await matchHistoryRepository.findRecordByIdempotencyKey(liveKey)) ||
      (await matchHistoryRepository.findRecordByIdempotencyKey(legacyKey));

    if (!existing) {
        existing = await persistCompletedMatch({
        sessionRoomId,
        teamA: entry.teamA,
        teamB: entry.teamB,
        sets: entry.sets || [],
        winnerSide: entry.winner,
        createdBy,
        completedAtMs,
        durationSec: entry.durationSec,
        participantSnapshots: buildParticipantSnapshots(
          entry.teamA,
          entry.teamB,
          registryEntries
        ),
        registryEntries,
        source: "legacy_import",
        legacyRoomIndex: i,
        idempotencyKey: liveKey,
        sportId: entry.sportId || options.sportId || undefined,
        rallySummary: entry.rallySummary || undefined,
      });
    } else {
      // Repair empty participants on an orphan record
      const parts = await matchHistoryRepository.listParticipantsForMatch(existing.matchId);
      if (!parts.length) {
        await persistCompletedMatch({
          sessionRoomId,
          teamA: entry.teamA,
          teamB: entry.teamB,
          sets: entry.sets || [],
          winnerSide: entry.winner,
          createdBy,
          completedAtMs,
          participantSnapshots: buildParticipantSnapshots(
            entry.teamA,
            entry.teamB,
            registryEntries
          ),
          registryEntries,
          idempotencyKey: existing.idempotencyKey || liveKey,
          sportId: entry.sportId || options.sportId || undefined,
        });
      }
    }

    if (existing?.matchId) {
      imported.push(existing);
      patched.push({ ...entry, canonicalMatchId: existing.matchId });
    } else {
      patched.push(entry);
    }
  }

  return { imported, patchedHistory: patched };
}

export { buildCanonicalMatchPayload };
